/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getTokenData } from 'src/utils/api';
import { transformFormDataToApiRequest } from 'src/views/transactions/manualInvoice/utils/transformManualInvoiceData';
import API from '../../../api/api';
import { formatDates } from 'src/utils/views/transactions';
import { LOADING_STATUSES, TRANSACTIONS_STATUS_FETCHING } from '../../../constants/statuses';
import { isResponseSuccess, formatFilters } from '../../../utils/api';
import { INITIAL_FILTERS_STATE } from '../constants/transactions';
import { showNotification } from 'src/utils/notifications';
import { getYYYYMMDDLocal } from 'src/utils/api';

export const loadTransactions = createAsyncThunk(
  'transactions/loadTransactions',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const { filters, page, per } = getState().transactionsReducer;
      const filtersWithFormattedDates = {
        ...filters,
        date_from: filters.date_from ? getYYYYMMDDLocal(new Date(filters.date_from)) : null,
        date_to: filters.date_to ? getYYYYMMDDLocal(new Date(filters.date_to)) : null,
      };

      const formattedFilters = formatFilters(filtersWithFormattedDates);

      const response = await API(getTokenData()).TRANSACTIONS.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });
      if (isResponseSuccess(response.status)) {
        return {
          data: response.data,
          totalItems: response.total,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const getTransactionsSettings = createAsyncThunk(
  'transactions/getTransactionsSettings',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).TRANSACTIONS.INVOICE.DETAILS.load();
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const postTransactionsSettings = createAsyncThunk(
  'transactions/postTransactionsSettings',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).TRANSACTIONS.INVOICE.DETAILS.create(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const createManualInvoice = createAsyncThunk(
  'transactions/createManualInvoice',
  async (formData, {
    rejectWithValue,
  }) => {
    try {
      const data = transformFormDataToApiRequest(formData);
      const response = await API(getTokenData()).TRANSACTIONS.INVOICE.MANUAL.create(data);
      if (isResponseSuccess(response.status)) {
        showNotification({
          type: 'success',
          text: 'Invoice created successfully',
        });
        return response.data;
      }
      const message = response.data?.error || 'Failed to create invoice';
      showNotification({ type: 'error', text: message });
      return rejectWithValue(message);
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const updateManualInvoice = createAsyncThunk(
  'transactions/updateManualInvoice',
  async ({ id, ...data }, {
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).TRANSACTIONS.INVOICE.MANUAL.update(id, data);
      if (isResponseSuccess(response.status)) {
        showNotification({
          type: 'success',
          text: 'Invoice updated successfully',
        });
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const updateStatusManualInvoice = createAsyncThunk(
  'transactions/manualInvoice/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).TRANSACTIONS.MANUAL_INVOICE.update(id, { status });

      if (isResponseSuccess(response.status)) {
        return response.data;
      }
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  filters: INITIAL_FILTERS_STATE,
  status: LOADING_STATUSES.IDLE,
  error: null,
};

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.per;
    },
    resetTransactions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTransactions.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadTransactions.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadTransactions.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(updateStatusManualInvoice.pending, (state, action) => {
        const { id } = action.meta.arg;
        const transaction = state.entity.find((transaction) => id === transaction.id);

        if (transaction) {
          transaction.status = TRANSACTIONS_STATUS_FETCHING.value;
        }
      })
      .addCase(updateStatusManualInvoice.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const transaction = state.entity.find((transaction) => id === transaction.id);

        if (transaction) {
          transaction.status = status;
          showNotification({ type: 'success', text: 'Status was successfully updated' })
        }
      })
      .addCase(updateStatusManualInvoice.rejected, (state, action) => {
        const { id, statusOriginal } = action.meta.arg;
        const transaction = state.entity.find((transaction) => id === transaction.id);

        if (transaction) {
          transaction.status = statusOriginal;
        }
      });
  },
});

export const {
  setFilters,
  setPageData,
  resetTransactions,
} = transactionsSlice.actions;
export default transactionsSlice.reducer;

export const transactionsFilterApply = createAsyncThunk(
  'transactions/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setFilters({
        ...newFilters,
      }));
      await dispatch(loadTransactions());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const transactionsChangePageAction = createAsyncThunk(
  'transactions/transactionsChangePageAction',
  async ({ page = 0, per = 0 }, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData({ page, per }));
      await dispatch(loadTransactions());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const getInvoiceAction = (id = '') => async (dispatch, getState) => {
  try {
    const response = await API(getTokenData()).TRANSACTIONS.INVOICE.load(id);
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    // Create object URL
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    const filename = `invoice-${id}.pdf`;
    link.setAttribute('download', filename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    console.error('Error downloading invoice:', error);
  }
};

export const copyLinkPdfAction = (link = '') => async () => {
  navigator.clipboard.writeText(link).then(() => {
    showNotification({
      type: 'info',
      text: 'Payment URL was copied to clipboard',
      timeout: 3000,
    });
  })
};

export const exportTransactions = createAsyncThunk(
  'transactions/export/csv',
  async (_, {
    getState,
    rejectWithValue,
  }) => {
    try {
      // remap transactions filtes to transactions report export method params
      const { filters } = getState().transactionsReducer;
      const { type, ...formattedFilters } = formatDates(formatFilters(filters));
      formattedFilters.types = type;

      const exportParams = [
        'user_id',
        'email',
        'types',
        'status',
        'sort',
        'date_from',
        'date_to',
      ].reduce((result, key) => {
        const value = formattedFilters[key];

        if (value) {
          result[key] = value;
        }

        return result;
      }, {});

      const response = await API(getTokenData()).TRANSACTIONS.REPORT.exportReport(exportParams);

      if (isResponseSuccess(response.status)) {
        showNotification({
          type: 'success',
          text: 'Export started! CSV file will be sent to your email.',
          timeout: 5000,
        });
        return response.data;
      }
      return {};
    } catch (error) {
      console.log('# err:', error);
      return rejectWithValue(error.toString);
    }
  },
);
